// MF-568 — Card 'pat-progress-pairs' (2A): paired scores + category transitions. One row per student: an open dot
// (previous score) joined to a filled dot (latest) on the PAT scale with the achievement bands shaded behind, then
// score change, stanine and band transitions. Students with a single result are listed apart, never charted.
// Rows: { student_id, name, score, band, stanine, test_date, prev_score, prev_band, prev_stanine, prev_test_date, change }.
import { DetailCard, Fact, FactRow } from './PatDetail'
import { useMemo, useState } from 'react'
import { Box, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { SUMMARY_PALETTE, INK, FONT } from '../boardTheme'
import {
  STANINE_NOTE,
  bandLow,
  bandHigh,
  byBand,
  isNum,
  fmtStanine,
  fmtChange,
  fmtDate,
  elapsedText,
  splitPairs,
  changeOf
} from './patProgressShared'

const UP = SUMMARY_PALETTE.good
const DOWN = SUMMARY_PALETTE.bad
const FLAT = SUMMARY_PALETTE.faint
const COLS = 'minmax(90px, 150px) minmax(220px, 1fr) 64px 72px 112px'
const keyActivate = (fn) => (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault()
    fn()
  }
}

const Dot = ({ pct, filled, color, label }) => (
  <Box
    title={label}
    sx={{
      position: 'absolute',
      left: `${pct}%`,
      top: '50%',
      width: 11,
      height: 11,
      transform: 'translate(-50%, -50%)',
      borderRadius: '50%',
      boxSizing: 'border-box',
      border: `2px solid ${color}`,
      bgcolor: filled ? color : '#fff'
    }}
  />
)

const PatProgressPairsCard = ({ data }) => {
  const rows = useMemo(() => data?.rows || [], [data])
  const { matched, single } = useMemo(() => splitPairs(rows), [rows])
  const [sortBy, setSortBy] = useState('score')
  const [selected, setSelected] = useState(null)

  const sorted = useMemo(() => {
    const list = [...matched]
    if (sortBy === 'change') list.sort((a, b) => changeOf(b) - changeOf(a) || a.name.localeCompare(b.name))
    else list.sort((a, b) => b.score - a.score || a.name.localeCompare(b.name))
    return list
  }, [matched, sortBy])

  const { lo, hi, bands } = useMemo(() => {
    const names = [
      ...new Set(matched.flatMap((r) => [r.band, r.prev_band]).filter((b) => bandLow(b) !== null))
    ].sort(byBand)
    const scores = matched.flatMap((r) => [Number(r.score), Number(r.prev_score)])
    const min = Math.min(...scores, ...names.map(bandLow))
    const max = Math.max(...scores, ...names.map(bandHigh))
    const from = Math.floor(min) - 1
    const to = Math.ceil(max) + 1
    return {
      lo: from,
      hi: to,
      bands: names.map((b) => ({
        band: b,
        from: Math.max(from, bandLow(b) - 0.5),
        to: Math.min(to, bandHigh(b) + 0.5)
      }))
    }
  }, [matched])

  const pct = (v) => ((Number(v) - lo) / (hi - lo)) * 100
  const ticks = useMemo(() => {
    const step = hi - lo > 60 ? 20 : 10
    const out = []
    for (let t = Math.ceil(lo / step) * step; t <= hi; t += step) out.push(t)
    return out
  }, [lo, hi])
  const noStanine = matched.length > 0 && matched.every((r) => !isNum(r.stanine) && !isNum(r.prev_stanine))
  const sel = sorted.find((r) => r.student_id === selected) || null

  const colorOf = (d) => (d > 0 ? UP : d < 0 ? DOWN : FLAT)

  return (
    <Box sx={{ height: '100%', overflow: 'auto', minWidth: 0, fontFamily: FONT }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          mb: 1
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            fontSize: 12,
            color: SUMMARY_PALETTE.muted
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                border: `2px solid ${UP}`,
                boxSizing: 'border-box'
              }}
            />
            Previous
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: UP }} />
            Latest
          </Box>
          <Box>Shaded columns are achievement bands</Box>
        </Box>
        <ToggleButtonGroup
          size='small'
          exclusive
          value={sortBy}
          onChange={(_, v) => v && setSortBy(v)}
          aria-label='Sort students'
        >
          <ToggleButton value='score' sx={{ textTransform: 'none', fontSize: 12, py: 0.25 }}>
            Latest score
          </ToggleButton>
          <ToggleButton value='change' sx={{ textTransform: 'none', fontSize: 12, py: 0.25 }}>
            Observed change
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {matched.length > 0 && (
        <Box sx={{ minWidth: 520 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: COLS,
              columnGap: 1,
              alignItems: 'end',
              fontSize: 11,
              color: SUMMARY_PALETTE.muted,
              pb: 0.5
            }}
          >
            <Box>Student</Box>
            <Box sx={{ position: 'relative', height: 16 }}>
              {ticks.map((t) => (
                <Box key={t} sx={{ position: 'absolute', left: `${pct(t)}%`, transform: 'translateX(-50%)' }}>
                  {t}
                </Box>
              ))}
            </Box>
            <Box sx={{ textAlign: 'right' }}>Change</Box>
            <Box sx={{ textAlign: 'center' }}>Stanine</Box>
            <Box sx={{ textAlign: 'center' }}>Band</Box>
          </Box>
          {sorted.map((r) => {
            const d = changeOf(r)
            const active = r.student_id === selected
            const pick = () => setSelected(active ? null : r.student_id)
            return (
              <Box
                key={r.student_id}
                role='button'
                tabIndex={0}
                aria-pressed={active}
                aria-label={`${r.name}: ${r.prev_score} to ${r.score}, change ${fmtChange(d)}`}
                onClick={pick}
                onKeyDown={keyActivate(pick)}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: COLS,
                  columnGap: 1,
                  alignItems: 'center',
                  minHeight: 28,
                  borderTop: `1px solid ${SUMMARY_PALETTE.grid}`,
                  cursor: 'pointer',
                  bgcolor: active ? '#eef3f9' : 'transparent',
                  '&:hover': { bgcolor: '#f5f8fb' },
                  '&:focus-visible': { outline: `2px solid ${UP}`, outlineOffset: -2 }
                }}
              >
                <Typography noWrap sx={{ fontSize: 13, color: SUMMARY_PALETTE.ink }}>
                  {r.name}
                </Typography>
                <Box sx={{ position: 'relative', height: 28 }}>
                  {bands.map((b, i) => (
                    <Box
                      key={b.band}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: `${pct(b.from)}%`,
                        width: `${pct(b.to) - pct(b.from)}%`,
                        bgcolor: i % 2 ? '#eef3f9' : '#f7f9fc'
                      }}
                    />
                  ))}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      height: 2,
                      transform: 'translateY(-50%)',
                      left: `${Math.min(pct(r.prev_score), pct(r.score))}%`,
                      width: `${Math.abs(pct(r.score) - pct(r.prev_score))}%`,
                      bgcolor: colorOf(d)
                    }}
                  />
                  <Dot pct={pct(r.prev_score)} color={colorOf(d)} label={`Previous ${r.prev_score}`} />
                  <Dot pct={pct(r.score)} filled color={colorOf(d)} label={`Latest ${r.score}`} />
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, textAlign: 'right', color: colorOf(d) }}>
                  {fmtChange(d)}
                </Typography>
                <Typography sx={{ fontSize: 12, textAlign: 'center', color: INK.secondary }}>
                  {fmtStanine(r.prev_stanine)} → {fmtStanine(r.stanine)}
                </Typography>
                <Typography sx={{ fontSize: 12, textAlign: 'center', color: INK.secondary }}>
                  {r.prev_band} → {r.band}
                </Typography>
              </Box>
            )
          })}
        </Box>
      )}

      {sel && (
        <DetailCard title={sel.name} onClear={() => setSelected(null)}>
          <FactRow>
            <Fact
              label='Score'
              value={`${sel.prev_score} → ${sel.score}`}
              sub={`${fmtChange(changeOf(sel))} · ${fmtDate(sel.prev_test_date)} → ${fmtDate(sel.test_date)}${
                elapsedText(sel.prev_test_date, sel.test_date)
                  ? ` (${elapsedText(sel.prev_test_date, sel.test_date)} apart)`
                  : ''
              }`}
              tone={colorOf(changeOf(sel))}
            />
            <Fact label='Stanine' value={`${fmtStanine(sel.prev_stanine)} → ${fmtStanine(sel.stanine)}`} />
            <Fact
              label='Band'
              value={`${sel.prev_band} → ${sel.band}`}
              sub={
                sel.prev_year_level && sel.prev_year_level !== sel.year_level
                  ? `${sel.prev_year_level} → ${sel.year_level}`
                  : undefined
              }
            />
          </FactRow>
        </DetailCard>
      )}

      {noStanine && (
        <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted, mt: 1 }}>{STANINE_NOTE}</Typography>
      )}

      {single.length > 0 && (
        <Box sx={{ mt: 1.5, fontSize: 12, color: SUMMARY_PALETTE.muted }}>
          <b>
            Only one result: {single.length} student{single.length === 1 ? '' : 's'}
          </b>
          {' — '}
          {single.map((r) => `${r.name} (${r.score})`).join(', ')}
        </Box>
      )}

      <Typography sx={{ fontSize: 11, color: SUMMARY_PALETTE.faint, mt: 1.5, lineHeight: 1.5 }}>
        Connected dots show the score change; stanine and band are context, not alternative growth units. A
        rising score with an unchanged band still means a higher measured estimate; small changes need
        attention to uncertainty. Transitions read prior → latest.
      </Typography>
    </Box>
  )
}

export default PatProgressPairsCard
